import React from 'react';

interface PlanningToolbarProps {
  opStatus: 'OPEN' | 'FINAL_CLOSE';
  viewMode: 'TACTICAL' | 'CREW' | 'GROUND' | 'HISTORY' | 'MANIFEST' | 'ROUTE';
  selectedDate: string;
  onSetViewMode: (mode: 'TACTICAL' | 'CREW' | 'GROUND' | 'HISTORY' | 'MANIFEST' | 'ROUTE') => void;
  onSetSelectedDate: (value: string) => void;
  charterConfirmed: boolean;
  scheduledConfirmed: boolean;
  charterUnassignedCount: number;
  charterCrewInvalidCount: number;
  scheduledUnassignedCount: number;
  onConfirmCharter: () => void;
  onConfirmScheduled: () => void;
  todayPax: number;
  todaySectors: number;
  todayFuelEstimate: number;
  dateActivity: Record<string, { charter: number; scheduled: number }>;
  isDesktopWorkspace?: boolean;
  onOpenDesktopWorkspace?: (dateKey: string) => void;
}

const SummaryMini: React.FC<{ label: string; value: number | string }> = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-white px-2 py-1 text-right shadow-sm">
    <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.16em]">{label}</span>
    <span className="mt-0.5 block text-xs font-black text-slate-800">{value}</span>
  </div>
);

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const WEEK_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const parseUtcDate = (raw: string): Date => {
  if (!DATE_RE.test(raw)) return new Date();
  const parsed = new Date(`${raw}T00:00:00.000Z`);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const toDateInput = (date: Date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const shiftDateByDays = (dateKey: string, offsetDays: number): string => {
  const base = parseUtcDate(dateKey);
  const next = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth(), base.getUTCDate() + offsetDays));
  return toDateInput(next);
};

const toMonthKey = (dateKey: string): string => {
  if (DATE_RE.test(dateKey)) return dateKey.slice(0, 7);
  const parsed = parseUtcDate(dateKey);
  return `${parsed.getUTCFullYear()}-${String(parsed.getUTCMonth() + 1).padStart(2, '0')}`;
};

const buildMonthCells = (monthCursor: string): Array<{ key: string; inMonth: boolean; dayLabel: number }> => {
  const parsed = parseUtcDate(`${monthCursor}-01`);
  const year = parsed.getUTCFullYear();
  const month = parsed.getUTCMonth();
  const first = new Date(Date.UTC(year, month, 1));
  const days = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const startOffset = first.getUTCDay();
  const cells: Array<{ key: string; inMonth: boolean; dayLabel: number }> = [];

  for (let i = 0; i < startOffset; i += 1) {
    cells.push({ key: `pad-s-${i}`, inMonth: false, dayLabel: 0 });
  }
  for (let d = 1; d <= days; d += 1) {
    const dateKey = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    cells.push({ key: dateKey, inMonth: true, dayLabel: d });
  }
  while (cells.length % 7 !== 0) {
    cells.push({ key: `pad-e-${cells.length}`, inMonth: false, dayLabel: 0 });
  }

  return cells;
};

const PlanningToolbar: React.FC<PlanningToolbarProps> = ({
  opStatus,
  viewMode,
  selectedDate,
  onSetViewMode,
  onSetSelectedDate,
  charterConfirmed,
  scheduledConfirmed,
  charterUnassignedCount,
  charterCrewInvalidCount,
  scheduledUnassignedCount,
  onConfirmCharter,
  onConfirmScheduled,
  todayPax,
  todaySectors,
  todayFuelEstimate,
  dateActivity,
  isDesktopWorkspace = false,
  onOpenDesktopWorkspace,
}) => {
  const today = toDateInput(new Date());
  const selected = parseUtcDate(selectedDate);

  const [pendingWorkspaceDate, setPendingWorkspaceDate] = React.useState(selectedDate);
  const [monthCursor, setMonthCursor] = React.useState(() => toMonthKey(selectedDate));

  React.useEffect(() => {
    if (!DATE_RE.test(selectedDate)) return;
    setPendingWorkspaceDate(selectedDate);
    setMonthCursor(toMonthKey(selectedDate));
  }, [selectedDate]);

  const shiftPendingWorkspace = React.useCallback((offsetDays: number) => {
    setPendingWorkspaceDate((prev) => {
      const base = DATE_RE.test(prev) ? prev : selectedDate;
      const next = shiftDateByDays(base, offsetDays);
      setMonthCursor(toMonthKey(next));
      return next;
    });
  }, [selectedDate]);


  const monthLabel = parseUtcDate(`${monthCursor}-01`).toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
  const monthCells = React.useMemo(() => buildMonthCells(monthCursor), [monthCursor]);

  const activitySummary = React.useMemo(() => {
    let charter = 0;
    let scheduled = 0;
    (Object.values(dateActivity) as Array<{ charter: number; scheduled: number }>).forEach((v) => {
      charter += v.charter || 0;
      scheduled += v.scheduled || 0;
    });
    return {
      charter,
      scheduled,
      bothDays: (Object.values(dateActivity) as Array<{ charter: number; scheduled: number }>)
        .filter(v => (v.charter || 0) > 0 && (v.scheduled || 0) > 0).length,
    };
  }, [dateActivity]);

  const pendingActivity = dateActivity[pendingWorkspaceDate] || { charter: 0, scheduled: 0 };
  const isPendingOpened = pendingWorkspaceDate === selectedDate;
  const openWorkspace = React.useCallback(() => {
    if (!isDesktopWorkspace && onOpenDesktopWorkspace) {
      onOpenDesktopWorkspace(pendingWorkspaceDate);
      return;
    }
    onSetViewMode('TACTICAL');
    onSetSelectedDate(pendingWorkspaceDate);
  }, [isDesktopWorkspace, onOpenDesktopWorkspace, onSetSelectedDate, onSetViewMode, pendingWorkspaceDate]);

  return (
    <>
      <div className={`rounded-2xl border p-3 shadow-sm transition-all ${opStatus === 'OPEN' ? 'border-indigo-200 bg-[linear-gradient(135deg,#eef4ff_0%,#f8faff_45%,#f5fffb_100%)]' : 'border-rose-200 bg-gradient-to-r from-rose-50 via-white to-slate-50'}`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1">
              <div className={`h-2.5 w-2.5 rounded-full ${opStatus === 'OPEN' ? 'bg-emerald-500 animate-pulse' : 'bg-rose-500'}`} />
              <p className={`text-[10px] font-black uppercase tracking-[0.16em] ${opStatus === 'OPEN' ? 'text-emerald-700' : 'text-rose-700'}`}>
                {opStatus.replace('_', ' ')}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-1 rounded-full border border-slate-200 bg-white px-1.5 py-1">
              {(['TACTICAL', 'CREW', 'GROUND', 'MANIFEST', 'HISTORY'] as const).map(mode => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => onSetViewMode(mode)}
                  className={`rounded-full px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.12em] transition-all ${
                    viewMode === mode
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  {mode === 'TACTICAL'
                    ? 'Flight Planner'
                    : mode === 'CREW'
                    ? 'DFO Control'
                    : mode === 'GROUND'
                    ? 'Ground Ops'
                    : mode === 'MANIFEST'
                    ? 'Manifest Hub'
                    : 'History'}
                </button>
              ))}
            </div>
            <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em] text-slate-600">
              Lockdown 23:00 EAT
            </span>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <SummaryMini label="Today Pax" value={todayPax} />
            <SummaryMini label="Today Sectors" value={todaySectors} />
            {viewMode === 'GROUND' && <SummaryMini label="Fuel Est" value={`${todayFuelEstimate}L`} />}
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500">Full Calendar</p>
                <button
                  type="button"
                  onClick={() => {
                    const cur = parseUtcDate(`${monthCursor}-01`);
                    const prev = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() - 1, 1));
                    setMonthCursor(`${prev.getUTCFullYear()}-${String(prev.getUTCMonth() + 1).padStart(2, '0')}`);
                  }}
                  className="rounded-lg border border-slate-200 px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
                >
                  Prev Month
                </button>
                <span className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-600">{monthLabel}</span>
                <button
                  type="button"
                  onClick={() => {
                    const cur = parseUtcDate(`${monthCursor}-01`);
                    const next = new Date(Date.UTC(cur.getUTCFullYear(), cur.getUTCMonth() + 1, 1));
                    setMonthCursor(`${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}`);
                  }}
                  className="rounded-lg border border-slate-200 px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700 hover:bg-slate-50"
                >
                  Next Month
                </button>
              </div>

              <div className="flex flex-wrap items-center gap-2 text-[10px] font-black uppercase tracking-[0.12em]">
                <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-indigo-700">Charter {activitySummary.charter}</span>
                <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700">Scheduled {activitySummary.scheduled}</span>
                <span className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-violet-50 px-2 py-1 text-violet-700">Both Days {activitySummary.bothDays}</span>
              </div>
            </div>

            <div className="mb-2 grid grid-cols-7 gap-2">
              {WEEK_LABELS.map((label) => (
                <div key={label} className="px-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">
                  {label}
                </div>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-2">
              {monthCells.map((cell) => {
                if (!cell.inMonth) return <div key={cell.key} className="h-20 rounded-lg border border-transparent" />;
                const active = dateActivity[cell.key] || { charter: 0, scheduled: 0 };
                const isPending = cell.key === pendingWorkspaceDate;
                const isOpened = cell.key === selectedDate;
                const hasCharter = active.charter > 0;
                const hasScheduled = active.scheduled > 0;
                const dayTotal = active.charter + active.scheduled;
                const dayTooltip = `${cell.key}: ${active.charter} charter sector(s), ${active.scheduled} scheduled booking(s), total ${dayTotal}`;

                return (
                  <button
                    key={cell.key}
                    type="button"
                    title={dayTooltip}
                    aria-label={dayTooltip}
                    onClick={() => setPendingWorkspaceDate(cell.key)}
                    className={`h-20 rounded-lg border px-2 py-1 text-left transition ${
                      isPending
                        ? 'border-indigo-300 bg-indigo-50'
                        : hasCharter && hasScheduled
                        ? 'border-violet-200 bg-violet-50/60 hover:border-violet-300'
                        : hasCharter
                        ? 'border-indigo-200 bg-indigo-50/60 hover:border-indigo-300'
                        : hasScheduled
                        ? 'border-emerald-200 bg-emerald-50/60 hover:border-emerald-300'
                        : 'border-slate-200 bg-slate-50 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <p className="text-sm font-black text-slate-800">{cell.dayLabel}</p>
                      {isOpened && (
                        <span className="rounded-full border border-indigo-200 bg-white px-1 py-0.5 text-[8px] font-black uppercase tracking-[0.1em] text-indigo-700">
                          Open
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-1">
                      <span className={`h-2 w-2 rounded-full ${hasCharter ? 'bg-indigo-500' : 'bg-slate-300'}`} title="Charter" />
                      <span className={`h-2 w-2 rounded-full ${hasScheduled ? 'bg-emerald-500' : 'bg-slate-300'}`} title="Scheduled" />
                      <span className="ml-1 text-[9px] font-black text-slate-500">{dayTotal}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-1 text-[8px] font-black uppercase tracking-[0.08em]">
                      <span className="rounded-full border border-indigo-200 bg-indigo-50 px-1 py-0.5 text-indigo-700">C {active.charter}</span>
                      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-1 py-0.5 text-emerald-700">S {active.scheduled}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">Selected Day</p>
            <p className="mt-1 text-base font-black text-slate-900">{pendingWorkspaceDate}</p>
            <p className="mt-1 text-[11px] font-semibold text-slate-600">
              Click any day on the calendar, then open workspace.
            </p>
            <div className="mt-4 space-y-2">
              <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-3 py-2">
                <p className="text-[10px] font-black uppercase tracking-[0.12em] text-indigo-700">Charter Missions</p>
                <p className="text-2xl font-black text-indigo-700">{pendingActivity.charter}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-[10px] font-black uppercase tracking-[0.12em] text-emerald-700">Scheduled Tickets</p>
                <p className="text-2xl font-black text-emerald-700">{pendingActivity.scheduled}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={openWorkspace}
              disabled={isDesktopWorkspace}
              className={`mt-4 w-full rounded-xl px-3 py-2 text-[10px] font-black uppercase tracking-[0.14em] transition-all ${
                isDesktopWorkspace
                  ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                  : isPendingOpened
                  ? 'bg-slate-800 text-white hover:bg-slate-700'
                  : 'bg-indigo-600 text-white hover:bg-indigo-500'
              }`}
            >
              {isDesktopWorkspace ? 'Desktop Workspace Active' : isPendingOpened ? 'Open Workspace' : 'Open Day Workspace'}
            </button>
          </div>
        </div>
      </div>

    </>
  );
};

export default PlanningToolbar;
